/**
 * Minimal namespaced storage so we never collide with other Fasto apps.
 *
 * As of Stage 11, the canonical source of truth for per-user prefs / saved
 * views / store lists lives on the server (`/api/bi/settings/`). The
 * helpers in this module are now a *cache* that:
 *   - lets the SPA render instantly on cold boot from last-known values,
 *   - is namespaced by the authenticated user's `sub` so two users on the
 *     same browser don't see each other's preferences,
 *   - is refreshed in-place via `storage.applyServerBlob(...)` whenever
 *     the server settings module receives a fresh response.
 *
 * The refresh token still lives in this module (memory-aware).
 */
const BASE_NS = 'fasto-bi:v1';
// Non-namespaced key that remembers which user namespace was last active
// in this browser. Read at module-import time so per-user prefs (e.g.
// `default_range`, `landing_path`) are available on cold boot — *before*
// AuthProvider mounts and re-runs `setStorageUser` after the JWT decodes.
// Without this, the very first `storage.getPrefs()` always returned the
// global defaults, which leaked into module-level snapshots in
// `state/filters.ts` and the router's <Landing> redirect.
const ACTIVE_NS_KEY = `${BASE_NS}:active-ns`;

function readPersistedNs(): string {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(ACTIVE_NS_KEY) : null;
    return v && v.startsWith(`${BASE_NS}:u:`) ? v : BASE_NS;
  } catch {
    return BASE_NS;
  }
}

// User-scoped namespace. Set by AuthProvider via `setStorageUser` once the
// access token decodes; reset to the shared base when logged out. We seed
// it from localStorage so cold-boot reads land in the correct scope.
let activeNs: string = readPersistedNs();
const subscribers = new Set<() => void>();

function nsKey(suffix: string): string {
  return `${activeNs}:${suffix}`;
}

const REFRESH_KEY = `${BASE_NS}:refresh`; // never user-scoped — auth bootstrap needs it before we know the user.
const SUFFIX = {
  THEME: 'theme',
  STORE: 'default-store',
  FAV_STORES: 'fav-stores',
  RECENT_STORES: 'recent-stores',
  COMPARE: 'compare',
  SAVED_VIEWS: 'saved-views',
  PREFS: 'prefs',
  NAV_SECTIONS: 'nav-sections',
} as const;

export interface SavedView {
  id: string;
  name: string;
  /** Encoded query string (same shape as `filtersToQuery` output). */
  query: string;
  /** Wall-clock created/updated timestamp for UI ordering & age display. */
  updated_at: string;
  /** Optional emoji-free icon hint (lucide name) — kept as a string so storage stays serialisable. */
  icon?: string;
  /** Optional pinned flag — pinned views show first and have a star indicator. */
  pinned?: boolean;
}

export interface UserPrefs {
  /** Default range preset to apply on cold boot if URL has no range param. */
  default_range: 'today' | 'yesterday' | 'this-week' | 'last-week' | '7d' | '30d' | 'mtd' | null;
  /** Default compare mode applied on first load. */
  default_compare: 'previous-period' | 'previous-year' | 'none' | null;
  /** Day boundary used by BI report requests. */
  time_range_mode: 'standard' | 'business';
  /** Background refresh cadence for KPIs (ms). 0 = disabled. */
  refresh_ms: number;
  /** Allow tasteful UI animations (false respects user pref but lets them force-disable). */
  animations: boolean;
  /** Show the welcome card on Overview. Some power users prefer it hidden. */
  show_welcome: boolean;
  /** Run the Stage 3 alert engine globally (Notification + chime + toasts). */
  alerts_enabled?: boolean;
  /** Mirror local engine hits to the Stage 9 team inbox (default true). */
  share_alerts_with_team?: boolean;
  /** Path the user is sent to on cold boot (defaults to `/`). */
  landing_path?: string;
  /** How big numbers render across KPI tiles. */
  kpi_format?: 'compact' | 'exact';
  /** Show "Why?" popovers on KPI cards. */
  why_popovers?: boolean;
  /**
   * Row height across tables. Ops users watching a queue want as many rows
   * on screen as possible; analysts reading one row at a time do not.
   */
  density?: 'comfortable' | 'compact';
}

const DEFAULT_PREFS: UserPrefs = {
  default_range: '7d',
  default_compare: 'previous-period',
  time_range_mode: 'standard',
  refresh_ms: 60_000,
  animations: true,
  show_welcome: true,
  alerts_enabled: false,
  share_alerts_with_team: true,
  landing_path: '/',
  kpi_format: 'compact',
  why_popovers: true,
  density: 'comfortable',
};

function safeJson<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function safeIntArray(arr: unknown): number[] {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function notify() {
  subscribers.forEach((fn) => {
    try {
      fn();
    } catch {
      /* noop */
    }
  });
}

/**
 * Switch the active per-user namespace. Pass `null` on logout to fall back
 * to the shared base namespace. Notifies subscribers so reactive consumers
 * (zustand stores, settings page) can re-read.
 */
export function setStorageUser(sub: string | number | null) {
  const next = sub != null && String(sub).length > 0 ? `${BASE_NS}:u:${sub}` : BASE_NS;
  // Persist the choice so the next cold boot can restore it before any
  // module-level reads happen (see `readPersistedNs` above).
  try {
    if (next === BASE_NS) localStorage.removeItem(ACTIVE_NS_KEY);
    else localStorage.setItem(ACTIVE_NS_KEY, next);
  } catch {
    /* private mode etc */
  }
  if (next === activeNs) return;
  activeNs = next;
  notify();
}

/** Subscribe to namespace + applyServerBlob events. Returns an unsubscribe. */
export function subscribeStorage(fn: () => void): () => void {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}

export const storage = {
  getRefresh(): string | null {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },
  setRefresh(token: string | null) {
    try {
      if (token) localStorage.setItem(REFRESH_KEY, token);
      else localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* private mode etc */
    }
  },
  getTheme(): 'light' | 'dark' | null {
    try {
      const v = localStorage.getItem(nsKey(SUFFIX.THEME));
      return v === 'light' || v === 'dark' ? v : null;
    } catch {
      return null;
    }
  },
  setTheme(theme: 'light' | 'dark') {
    try {
      localStorage.setItem(nsKey(SUFFIX.THEME), theme);
    } catch {
      /* noop */
    }
  },
  getDefaultStore(): number | null {
    try {
      const v = localStorage.getItem(nsKey(SUFFIX.STORE));
      const n = v ? Number(v) : NaN;
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  },
  setDefaultStore(id: number | null) {
    try {
      if (id == null) localStorage.removeItem(nsKey(SUFFIX.STORE));
      else localStorage.setItem(nsKey(SUFFIX.STORE), String(id));
    } catch {
      /* noop */
    }
  },
  getFavStores(): number[] {
    try {
      return safeIntArray(safeJson(localStorage.getItem(nsKey(SUFFIX.FAV_STORES)), [] as number[]));
    } catch {
      return [];
    }
  },
  setFavStores(ids: number[]) {
    try {
      localStorage.setItem(nsKey(SUFFIX.FAV_STORES), JSON.stringify(safeIntArray(ids).slice(0, 12)));
    } catch {
      /* noop */
    }
  },
  getRecentStores(): number[] {
    try {
      return safeIntArray(safeJson(localStorage.getItem(nsKey(SUFFIX.RECENT_STORES)), [] as number[]));
    } catch {
      return [];
    }
  },
  pushRecentStore(id: number | null) {
    if (id == null) return;
    try {
      const prev = safeIntArray(safeJson(localStorage.getItem(nsKey(SUFFIX.RECENT_STORES)), [] as number[]));
      const next = [id, ...prev.filter((n) => n !== id)].slice(0, 6);
      localStorage.setItem(nsKey(SUFFIX.RECENT_STORES), JSON.stringify(next));
    } catch {
      /* noop */
    }
  },
  getCompareMode(): 'previous-period' | 'previous-year' | 'none' | null {
    try {
      const v = localStorage.getItem(nsKey(SUFFIX.COMPARE));
      if (v === 'previous-period' || v === 'previous-year' || v === 'none') return v;
      return null;
    } catch {
      return null;
    }
  },
  setCompareMode(mode: 'previous-period' | 'previous-year' | 'none') {
    try {
      localStorage.setItem(nsKey(SUFFIX.COMPARE), mode);
    } catch {
      /* noop */
    }
  },

  getSavedViews(): SavedView[] {
    try {
      const raw = safeJson<unknown>(localStorage.getItem(nsKey(SUFFIX.SAVED_VIEWS)), []);
      if (!Array.isArray(raw)) return [];
      return raw
        .filter((v): v is SavedView =>
          !!v && typeof v === 'object' &&
          typeof (v as SavedView).id === 'string' &&
          typeof (v as SavedView).name === 'string' &&
          typeof (v as SavedView).query === 'string',
        )
        .slice(0, 30);
    } catch {
      return [];
    }
  },
  setSavedViews(views: SavedView[]) {
    try {
      localStorage.setItem(nsKey(SUFFIX.SAVED_VIEWS), JSON.stringify(views.slice(0, 30)));
    } catch {
      /* noop */
    }
  },

  /**
   * Which sidebar sections the user left open. Persisted because the sidebar
   * used to reset to all-collapsed on every mount, so each navigation hid the
   * nav the user had just opened.
   */
  getNavSections(): string[] {
    try {
      const raw = safeJson<unknown>(localStorage.getItem(nsKey(SUFFIX.NAV_SECTIONS)), null);
      if (!Array.isArray(raw)) return [];
      return raw.filter((v): v is string => typeof v === 'string');
    } catch {
      return [];
    }
  },
  /** `null` clears the stored set so defaults apply again. */
  setNavSections(ids: string[] | null) {
    try {
      if (ids == null) localStorage.removeItem(nsKey(SUFFIX.NAV_SECTIONS));
      else localStorage.setItem(nsKey(SUFFIX.NAV_SECTIONS), JSON.stringify(ids));
    } catch {
      /* noop */
    }
  },
  /** Distinguishes "never chosen" from "deliberately collapsed everything". */
  hasNavSections(): boolean {
    try {
      return localStorage.getItem(nsKey(SUFFIX.NAV_SECTIONS)) != null;
    } catch {
      return false;
    }
  },

  getPrefs(): UserPrefs {
    try {
      const merged = { ...DEFAULT_PREFS, ...safeJson<Partial<UserPrefs>>(localStorage.getItem(nsKey(SUFFIX.PREFS)), {}) };
      // Clamp refresh_ms so a bad write can't peg the UI to a 100ms refetch loop.
      if (!Number.isFinite(merged.refresh_ms) || merged.refresh_ms < 0) merged.refresh_ms = DEFAULT_PREFS.refresh_ms;
      if (merged.refresh_ms > 0 && merged.refresh_ms < 5_000) merged.refresh_ms = 5_000;
      return merged;
    } catch {
      return { ...DEFAULT_PREFS };
    }
  },
  setPrefs(next: Partial<UserPrefs>) {
    try {
      const cur = this.getPrefs();
      localStorage.setItem(nsKey(SUFFIX.PREFS), JSON.stringify({ ...cur, ...next }));
    } catch {
      /* noop */
    }
  },
  resetPrefs() {
    try {
      localStorage.removeItem(nsKey(SUFFIX.PREFS));
    } catch {
      /* noop */
    }
  },

  /**
   * Mirror a freshly-fetched server blob into the cache so
   * `storage.getPrefs()` and friends keep returning sensible values for
   * legacy callers. Called from the settings module on every successful
   * GET / PATCH.
   */
  applyServerBlob(blob: {
    prefs?: Partial<UserPrefs>;
    saved_views?: SavedView[];
    default_store?: number | null;
    fav_stores?: number[];
    recent_stores?: number[];
    compare_mode?: 'previous-period' | 'previous-year' | 'none' | null;
    theme?: 'light' | 'dark';
    [k: string]: unknown;
  }) {
    let changed = false;
    try {
      if (blob.prefs && typeof blob.prefs === 'object') {
        const cur = this.getPrefs();
        const nextStr = JSON.stringify({ ...cur, ...blob.prefs });
        const key = nsKey(SUFFIX.PREFS);
        const prev = localStorage.getItem(key);
        if (nextStr !== prev) {
          localStorage.setItem(key, nextStr);
          changed = true;
        }
      }
      if (Array.isArray(blob.saved_views)) {
        const nextStr = JSON.stringify(blob.saved_views.slice(0, 30));
        const key = nsKey(SUFFIX.SAVED_VIEWS);
        const prev = localStorage.getItem(key);
        if (nextStr !== prev) {
          localStorage.setItem(key, nextStr);
          changed = true;
        }
      }
      if ('default_store' in blob) {
        const next = typeof blob.default_store === 'number' ? blob.default_store : null;
        const key = nsKey(SUFFIX.STORE);
        const prev = localStorage.getItem(key);
        const prevNum = prev ? Number(prev) : null;
        if (next !== prevNum) {
          if (next == null) localStorage.removeItem(key);
          else localStorage.setItem(key, String(next));
          changed = true;
        }
      }
      if (Array.isArray(blob.fav_stores)) {
        const nextStr = JSON.stringify(safeIntArray(blob.fav_stores).slice(0, 12));
        const key = nsKey(SUFFIX.FAV_STORES);
        const prev = localStorage.getItem(key);
        if (nextStr !== prev) {
          localStorage.setItem(key, nextStr);
          changed = true;
        }
      }
      if (Array.isArray(blob.recent_stores)) {
        try {
          const nextStr = JSON.stringify(safeIntArray(blob.recent_stores));
          const key = nsKey(SUFFIX.RECENT_STORES);
          const prev = localStorage.getItem(key);
          if (nextStr !== prev) {
            localStorage.setItem(key, nextStr);
            changed = true;
          }
        } catch {
          /* noop */
        }
      }
      if (
        blob.compare_mode === 'previous-period' ||
        blob.compare_mode === 'previous-year' ||
        blob.compare_mode === 'none'
      ) {
        const key = nsKey(SUFFIX.COMPARE);
        const prev = localStorage.getItem(key);
        if (prev !== blob.compare_mode) {
          localStorage.setItem(key, blob.compare_mode);
          changed = true;
        }
      }
      if (blob.theme === 'light' || blob.theme === 'dark') {
        const key = nsKey(SUFFIX.THEME);
        const prev = localStorage.getItem(key);
        if (prev !== blob.theme) {
          localStorage.setItem(key, blob.theme);
          changed = true;
        }
      }
    } catch {
      /* noop */
    } finally {
      if (changed) notify();
    }
  },

  /**
   * Snapshot the current cache as a server-shaped blob — used as a one-time
   * backfill on first login when the server returns an empty `data: {}`.
   */
  snapshotForServer(): {
    prefs?: Partial<UserPrefs>;
    saved_views?: SavedView[];
    default_store?: number | null;
    fav_stores?: number[];
    recent_stores?: number[];
    compare_mode?: 'previous-period' | 'previous-year' | 'none' | null;
    theme?: 'light' | 'dark';
  } {
    const out: ReturnType<typeof storage.snapshotForServer> = {};
    try {
      const rawPrefs = localStorage.getItem(nsKey(SUFFIX.PREFS));
      if (rawPrefs) out.prefs = safeJson<Partial<UserPrefs>>(rawPrefs, {});
      const views = this.getSavedViews();
      if (views.length) out.saved_views = views;
      const ds = this.getDefaultStore();
      if (ds != null) out.default_store = ds;
      const favs = this.getFavStores();
      if (favs.length) out.fav_stores = favs;
      const recents = this.getRecentStores();
      if (recents.length) out.recent_stores = recents;
      const cmp = this.getCompareMode();
      if (cmp) out.compare_mode = cmp;
      const theme = this.getTheme();
      if (theme) out.theme = theme;
    } catch {
      /* noop */
    }
    return out;
  },

  /** Wipe everything Fasto BI owns in localStorage (keeps refresh token). */
  clearAll(opts: { keepAuth?: boolean } = {}) {
    try {
      const refresh = opts.keepAuth ? localStorage.getItem(REFRESH_KEY) : null;
      // Remove every key under the active namespace and the legacy base
      // namespace so old installs get tidied on first sign-in.
      const namespaces = activeNs === BASE_NS ? [BASE_NS] : [BASE_NS, activeNs];
      const suffixes = Object.values(SUFFIX);
      for (const ns of namespaces) {
        for (const sfx of suffixes) {
          localStorage.removeItem(`${ns}:${sfx}`);
        }
      }
      if (refresh != null) localStorage.setItem(REFRESH_KEY, refresh);
    } catch {
      /* noop */
    } finally {
      notify();
    }
  },
};

export const DEFAULT_USER_PREFS = DEFAULT_PREFS;
